import React, {forwardRef, useImperativeHandle, useState} from "react";
import {useTranslation} from "react-i18next";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {OutlinedInputForm} from "./outlined_input";
import Api from "../../../../core/services/api/api";
import useAccessLevels from "../../../hooks/use-access-levels";

const NCRInput = ({setValidating, onDuplicateFound, onValidFound, manual, ignore, ...props}, ref) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.ncr-input'});
    const {packages} = useAccessLevels();
    const [NCRValidation, setNCRValidation] = useState({validating: false, message: null, error: false, ncr: null})

    /**
     * Exposes some intended logic to the parent component:
     * - resetting the validation
     */
    useImperativeHandle(ref, () => ({
        resetValidation: () => setNCRValidation({validating: false, message: null, error: false, ncr: null})
    }), [setNCRValidation])

    /**
     * Validates the NCR by checking if its a number and then validates it in the server
     * @param ncr {string}
     * @return {Promise<boolean>} whether the ncr is valid or not.
     */
    const validateNCR = async (ncr) => {
        if (ncr !== NCRValidation.ncr) {
            setNCRValidation(prevState => ({...prevState, ncr: ncr}));
        } else {
            return !NCRValidation.error;
        }
        if (manual && !ncr?.length) {
            setNCRValidation(prevState => ({
                ...prevState,
                error: true,
                message: ValidateMessages.required,
            }));
            return false;
        }
        if (!ncr?.length) {
            setNCRValidation(prevState => ({...prevState, error: false, message: null}));
            return true;
        }
        setNCRValidation(prevState => ({...prevState, validating: true}));
        if (ignore?.map(e => e.toLowerCase())?.includes(ncr?.toLowerCase() ?? '')) {
            return true;
        }
        if (!packages.ncr.manageReport) {
            return true;
        }
        if (setValidating) setValidating(true)
        const validateRes = await Api.checkNcrCodeDuplication(ncr);
        if (setValidating) setValidating(false)
        if (!!validateRes?.data && validateRes.data?.isDuplicated) {
            if (onDuplicateFound)
                onDuplicateFound(validateRes.data);
            setNCRValidation(prevState => ({
                ...prevState,
                validating: false,
                error: true,
                message: t('duplicateCode')
            }));
            return false;
        }
        if (onValidFound)
            onValidFound(ncr);
        setNCRValidation(prevState => ({...prevState, validating: false, error: false, message: null}));
        return true;
    }

    /**
     * Validates the NCR input.
     * @param value {string}
     * @return {string | null}
     */
    const validateNCRFormat = (value) => {
        if (manual && !value?.length) {
            return ValidateMessages.required;
        }
        if (!value?.length) {
            return null;
        }
        return null;
    }

    return (
        <>
            <OutlinedInputForm
                error={!!NCRValidation.error}
                helperText={!!NCRValidation.error ? NCRValidation.message : null}
                disabled={!manual}
                label={'NCR'}
                type={'number'}
                {...props}
                onBlur={(e) => validateNCR(e.target.value)}
                fieldProps={{
                    validate: validateNCRFormat
                }}
            />
        </>
    )
}

export default forwardRef(NCRInput);
